import { useEffect, useMemo, useState } from "react";
import omit from "lodash/fp/omit";
import classNames from "classnames";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import { SCREEN_SM } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import useSorting from "@rio-cloud/rio-uikit/useSorting";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import Toolbar, { ColumnDetailsMap, ColumnLabel } from "./Toolbar";
import Table from "./Table";
import { useT } from "@hooks";

export interface TableDataProps {
  id: number;
  date: string;
  name: string;
  type?: string;
  expenseType?: string;
  categoryName?: string;
  categoryId?: number;
  active?: boolean;
  predefined?: boolean;
}

const demoColumnsDetails: ColumnDetailsMap = {};

export type TableSettingsProps = {
  columnLabels: ColumnLabel;
  defaultColumnOrder: string[];
  viewType: TableViewTogglesViewType;
  data: TableDataProps[];
  totalData: number;
  onLoadMore: ({ size, page }: { size: number; page: number }) => void;
  openModal?: (open: boolean) => void;
  onEdit?: (
    id: number,
    name: string,
    type?: string,
    expenseType?: string,
    categoryName?: string,
    categoryId?: number
  ) => void;
  onDelete?: (id: number, name: string) => void;
  onActivateOption?: (id: number) => void;
  onDeleteFile?: (id: string) => void;
  onGetFile?: (id: string) => void;
  onObservation?: (id: string) => void;
  isLoading?: boolean;
};

const TableSettings = (props: TableSettingsProps) => {
  const t = useT();
  const {
    viewType: externalViewType,
    data,
    totalData,
    onLoadMore,
    onEdit,
    onDelete,
    onObservation,
    onDeleteFile,
    onGetFile,
    columnLabels,
    defaultColumnOrder,
    onActivateOption,
    isLoading = false,
  } = props;

  const [searchValue, setSearchValue] = useState("");

  const [columnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns] = useState<string[]>([]);
  const [columnsDetails] = useState(demoColumnsDetails);
  const [viewType] = useState(() =>
    window.innerWidth < SCREEN_SM
      ? TableViewToggles.VIEW_TYPE_SINGLE_CARD
      : externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const { sortedItems, sortKey, sortDirection, setSortKey, setSortDirection, toggleDirection } = useSorting(
    data,
    "date"
  );

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<TableDataProps>({
    tableData: data,
    idKey: "id" as keyof TableDataProps,
  });

  useEffect(() => {
    updateRowSelection();
  }, [searchValue, updateRowSelection]);

  const handleSearchValueChange = (newSearchValue: string) => setSearchValue(newSearchValue);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.dataset.sortby ?? null;
    if (newSortBy) {
      setSortKey(newSortBy as keyof TableDataProps);
    }
    if (newSortBy === sortKey) {
      toggleDirection();
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setSortKey(newSortBy as keyof TableDataProps);
    setSortDirection(newSortDir);
  };

  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = sortedItems.map((vehicle) => ({ ...withoutHidden(vehicle) })) as TableDataProps[];

  const searchResult = useMemo(() => {
    if (!searchValue) {
      return filteredRows;
    }
    return filteredRows.filter((row: Partial<TableDataProps>) =>
      columns.some((col) => {
        const value = row[col as keyof TableDataProps];
        if (value == null) {
          return false;
        }
        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [searchValue, filteredRows, columns]);

  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortKey,
      disabled: false,
    };
  });

  return (
    <div className="height-100pct">
      <Toolbar
        searchValue={searchValue}
        handleSearchValueChange={handleSearchValueChange}
        openModal={(open) => props.openModal?.(open)}
      />
      {sortKey && !isViewTypeTable && (
        <TableCardsSorting
          className="display-none display-block-sm"
          selectOptions={cardSortingSelectOptions}
          sortName={sortKey as string}
          sortOrder={sortDirection}
          onSortChange={handleCardSortChange}
        />
      )}
      <Table
        rows={rows}
        isViewTypeTable={isViewTypeTable}
        tableRef={tableRef}
        tableClassNames={tableClassNames}
        columns={columns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        sortKey={sortKey}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        onActiveRowChange={onActiveRowChange}
        onEdit={onEdit}
        onDelete={onDelete}
        onDeleteFile={onDeleteFile}
        onActivateOption={onActivateOption}
        onGetFile={onGetFile}
        onObservation={onObservation}
        isLoading={isLoading}
      />

      {rows?.length > 0 ? (
        <LoadMoreButton
          loaded={rows.length}
          total={totalData}
          onLoadMore={() =>
            onLoadMore({
              page: totalData / 10,
              size: rows.length + 10,
            })
          }
          loadMoreMessage={t("common.action.loadMore")}
          noMoreMessage={t("tolls.table.loadMore.noMore")}
        />
      ) : null}
    </div>
  );
};

export default TableSettings;
